<template>
  <div
    :class="componentClasses"
    :style="{ '--level': level }"
  >
    <template v-if="valueType === 'unknown'">
      <p>Unknown type</p>
    </template>

    <template v-else-if="valueType === 'object'">
      <FieldTitle
        :level="level"
        :name="name"
        :value-type="valueType"
        :array-index="arrayIndex"
        type="object"
      />

      <div class="field-object">
        <SchemaField
          v-for="(propSchema, key) in orderedProperties"
          :key="key"
          :schema="propSchema"
          :name="`${props.name}.${key}`"
          :level="level + 1"
          :current-value="props.currentValue?.[key]"
          :draft-value="props.draftValue?.[key]"
          :form-value="props.formValue?.[key]"
          :what-to-show="whatToShow"
          :show-confidence="showConfidence"
          @update:formData="payload => emit('update:formData', payload)"
          @update:childHasChanges="flag => containsChanges = flag"
          @update:containsObjectWithChanges="flag => childObjectHasChanges = flag"
        />
      </div>
    </template>

    <template v-else-if="valueType === 'array'">
      <FieldTitle
        :level="level"
        :name="name"
        :value-type="valueType"
        type="array"
      />

      <template v-if="zeroBasedArray.length > 0">
        <template
          v-for="index in zeroBasedArray"
        >
          <SchemaField
            :key="index"
            :schema="resolved.items"
            :name="`${name}[${index}]`"
            :level="level + 1"
            :current-value="props.currentValue?.[index]"
            :draft-value="props.draftValue?.[index]"
            :form-value="props.formValue?.[index]"
            :array-index="index"
            :what-to-show="whatToShow"
            :show-confidence="showConfidence"
            @update:formData="payload => emit('update:formData', payload)"
            @update:childHasChanges="flag => containsChanges = flag"
            @update:containsObjectWithChanges="flag => childObjectHasChanges = flag"
          />
        </template>
      </template>

      <FieldTitle
        v-else-if="whatToShow"
        is-empty
      >
        No entry
      </FieldTitle>
    </template>

    <template v-else>
      <div class="field-row">
        <FieldTitle
          :level="level"
          :name="name"
          :value-type="valueType"
        />

        <FieldChangeBadge
          :change-type="changeType"
          :conflict="fieldOptions?.length > 0"
        />

        <template v-if="valueType === 'enum'">
          <div class="field-control is-current">
            <div class="stk-select spacing-tight">
              <select
                v-model="localCurrent"
                class="field-input is-current"
                :class="{
                  'is-denied': saveDraft,
                }"
              >
                <option value="" />
                <option
                  v-for="option in resolved.enum"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </select>
              <Icon class="field-select-icon" />
            </div>
          </div>

          <Icon
            class="field-arrow"
            :class="{
              'is-denied': !saveDraft,
            }"
            icon="fas fa-arrow-right"
          />

          <template v-if="fieldOptions?.length">
            <FieldConflict
              :field-options="fieldOptions"
              :chosen="saveDraft"
              @update="handleConflictChoice"
            />
          </template>

          <template v-else>
            <div class="field-control is-draft">
              <div class="stk-select spacing-tight">
                <select
                  v-model="localDraft"
                  class="field-input is-draft"
                  :class="{
                    'is-denied': !saveDraft,
                  }"
                >
                  <option value="" />
                  <option
                    v-for="option in resolved.enum"
                    :key="option"
                    :value="option"
                  >
                    {{ option }}
                  </option>
                </select>
                <Icon class="field-select-icon" />
              </div>
            </div>
          </template>
        </template>

        <!-- Primitive Types: string, boolean, number, integer -->
        <template v-else>
          <div class="field-control is-current">
            <input
              v-model="localCurrent"
              class="field-input is-current"
              :class="{
                'stk-input spacing-tight': valueType !== 'boolean',
                'is-denied': saveDraft,
              }"
              :type="inputType"
            >
          </div>

          <Icon
            class="field-arrow"
            :class="{
              'is-denied': !saveDraft,
            }"
            icon="fas fa-arrow-right"
          />

          <template v-if="fieldOptions?.length">
            <FieldConflict
              :field-options="fieldOptions"
              :chosen="saveDraft"
              @update="handleConflictChoice"
            />
          </template>

          <template v-else>
            <div class="field-control is-draft">
              <input
                v-model="localDraft"
                class="field-input is-draft"
                :class="{
                  'stk-input spacing-tight': valueType !== 'boolean',
                  'is-denied': !saveDraft,
                }"
                :type="inputType"
              >
            </div>
          </template>
        </template>

        <div class="field-source">
          <span
            v-if="fieldOptions?.length"
            class="stk-size-3"
          >
            Multiple choice
          </span>
          <FieldSource
            v-else
            :source="fieldSource"
          />
        </div>

        <div class="field-toggle">
          <Toggle
            :model-value="saveDraft"
            @update:modelValue="saveDraft = !saveDraft"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch, computed } from 'vue';
import { Icon, Toggle } from '@stakeui';
import SchemaField from './SchemaField.vue';
import FieldTitle from './FieldTitle.vue';
import FieldConflict from './FieldConflict.vue';
import FieldChangeBadge from './FieldChangeBadge.vue';
import FieldSource from './FieldSource.vue';
import { useReconciliationStore } from '@frontend/ui/stores/reconciliationStore.js';
import { isEmptyObject } from '@frontend/shared/Util.js';

const props = defineProps({
  schema: Object,
  name: String,
  level: Number,
  currentValue: [String, Number, Boolean, Object, Array],
  draftValue: [String, Number, Boolean, Object, Array],
  formValue: [String, Number, Boolean, Object, Array],
  arrayIndex: Number,
  whatToShow: String,
  showConfidence: Array,
});

const reconciliationStore = useReconciliationStore();

const localCurrent = ref(props.currentValue);
const localDraft = ref(props.draftValue);
const containsChanges = ref(false);
const childObjectHasChanges = ref(false);

const fieldOptions = reconciliationStore.getFieldOptions(props.name);
const fieldSource = reconciliationStore.getFieldSource(props.name);
const fieldConfidence = computed(() => fieldSource?.confidence ?? '');

// We save the new draftValue only if
// - there is no source
// - the source is empty
// - the confidence score is high or medium
const saveDraft = ref(
    !fieldSource ||
  isEmptyObject(fieldSource) ||
  ['high', 'medium'].includes(fieldSource?.confidence),
);

const emit = defineEmits(['update:formData', 'update:childHasChanges', 'update:containsObjectWithChanges']);

// Normalize the value for comparison
// Case changes and leading or trailing spaces do not count as a change
const normalizeValue = val =>
  typeof val === 'string' ?
    val.trim().toLowerCase() :
    String(val ?? '');

// Figure out what the field type is
const resolved = computed(() => {
  if (props.schema?.anyOf) {
    const nonNull = props.schema.anyOf.find(opt => opt.type !== 'null');
    return nonNull || props.schema.anyOf[0];
  }
  return props.schema;
});

const valueType = computed(() => {
  const res = resolved.value;

  if (res.enum) return 'enum';

  if (res.type === 'array') return 'array';

  if (
    res.type === 'object' ||
    res.properties ||
    res.anyOf
  ) return 'object';

  // Fallback for primitives
  if (['string', 'number', 'integer', 'boolean'].includes(res.type)) {
    return res.type;
  }

  return 'unknown';
});

// Figure out what type of input type to show for primitive types
const inputType = computed(() => {
  const res = resolved.value;

  if (['number', 'integer'].includes(res.type)) {
    return 'number';
  }

  if (res.type === 'boolean') {
    return 'checkbox';
  }

  return 'text';
});

function isEmpty(value) {
  return value === null || value === '' || value === undefined;
}

// The type will determine how the field will show up.
// It will also determine if it will be hidden when the user
// only wants to display changes.
const changeType = computed(() => {
  const res = resolved.value;
  let result = '';

  if (['string', 'number', 'integer', 'boolean'].includes(res.type)) {
    const currEmpty = isEmpty(localCurrent.value);
    const draftEmpty = isEmpty(localDraft.value);

    if (currEmpty && draftEmpty) {
      result = 'both_empty';
    } else if (currEmpty && !draftEmpty) {
      result = 'addition';
    } else if (!currEmpty && draftEmpty) {
      result = 'removal';
    } else {
      const currVal = normalizeValue(localCurrent.value);
      const draftVal = normalizeValue(localDraft.value);
      result = currVal !== draftVal ? 'change' : 'same';
    }
  }

  return result;
});

const orderedProperties = computed(() => {
  let properties = resolved.value?.properties;

  if (!properties && resolved.value?.anyOf) {
    const nonNull = resolved.value.anyOf.find(opt => opt.type !== 'null');
    properties = nonNull?.properties;
  }

  if (!properties) return {};

  const result = Object.fromEntries(
      Object.entries(properties).sort(([_, v]) => {
        if (v.type === 'array') return 2;
        if (v.type === 'object' || v.properties || v.anyOf) return 1;
        return 0;
      }),
  );

  return result;
});

// If the current and draft values are arrays of different length,
// we show the largest one
const maxArrayLength = computed(() => {
  return Array.isArray(props.formValue) ? props.formValue.length : 0;
});

const zeroBasedArray = computed(() =>
  Array.from({ length: maxArrayLength.value }, (_, i) => i),
);

const componentClasses = computed(() => [
  'field-group',
  reconciliationStore.isPendingDelete(props.name) && 'marked-for-deletion',
  fieldOptions.length > 0 && 'has-conflicts',
  props.level === 0 && 'is-top-level',
  props.level > 0 && 'is-not-top-level',
  props.whatToShow && `show-${props.whatToShow}`,
  valueType.value ? `value-type-${valueType.value}` : '',
  changeType.value && fieldOptions.length === 0 ? `change-type-${changeType.value}` : '',
  containsChanges.value ? 'contains-changes' : 'does-not-have-changes',
  childObjectHasChanges.value ? 'child-object-has-changes' : '',
  fieldConfidence.value && `confidence-${fieldConfidence.value}`,
  (props.showConfidence?.length > 0) && 'show-confidence',
  ...(props.showConfidence || []).map(c => `show-${c}-confidence`),
]);

// Handlers
const handleConflictChoice = (value, source, confidence, explanation) => {
  if (value) {
    saveDraft.value = true;
    localDraft.value = value;
    reconciliationStore.addPendingSourceUpdate(props.name, {
      value,
      source,
      confidence,
      explanation,
    });
  } else {
    reconciliationStore.removePendingSourceUpdate(props.name);
  }
};

const updateFinalValue = () => {
  const finalToSave = saveDraft.value ? localDraft.value : localCurrent.value;
  const originalValue = saveDraft.value ? props.draftValue : props.currentValue;

  const normalizedFinal = normalizeValue(finalToSave);
  const normalizedOriginal = normalizeValue(originalValue);

  const hasChanged = normalizedFinal !== normalizedOriginal;

  // Update form data
  reconciliationStore.updateFormDataSection(props.name, finalToSave);

  // If the final stored value has changed,
  // we set the source as "Manual entry"
  // TODO: This doesn't work for selecting a field option-- should update the source to match the selected field option
  if (fieldOptions.length === 0) { // No conflicts
    if (hasChanged) {
    // maybe add flag that it was selected from field_options to distinguish
      reconciliationStore.addPendingSourceUpdate(props.name, {
        value: finalToSave,
        source: 'Manual entry.',
        confidence: 'unknown',
        explanation: 'This information was manually entered by you.',
      });
    } else {
      reconciliationStore.removePendingSourceUpdate(props.name);
    }
  }

  reconciliationStore.updateFormDataSection(props.name, finalToSave);
};

// Figure out which value to save in the end
watch([localCurrent, localDraft], updateFinalValue);

// If the user changes the localDraft,
// we need to save that change
watch(localDraft, () => {
  saveDraft.value = true;
}, { deep: true });

// If the user toggles the field, we need to store the other value
watch(saveDraft, updateFinalValue);

// If a field is a change:
// - it means it contains changes
// - it also tells the parent object that a child of it has changes
// If there's no change, we keep the current dossier value, not the draft one.
watch(changeType, newVal => {
  if (['addition', 'change', 'removal'].includes(newVal)) {
    containsChanges.value = true;
    emit('update:childHasChanges', true);
  } else if (newVal === 'same') {
    saveDraft.value = false;
    containsChanges.value = false;
    emit('update:childHasChanges', false);
  }
}, { immediate: true });

// If an object has a child with changes,
// it will propagate to it s own parent as well
watch(containsChanges, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    emit('update:containsObjectWithChanges', true);
  }
}, { immediate: true });

watch(childObjectHasChanges, (newVal, oldVal) => {
  if (newVal && !oldVal) {
    emit('update:containsObjectWithChanges', true);
  }
}, { immediate: true });
</script>

<style scoped>
.field-group {
  --level: 0;
  position: relative;

  &.marked-for-deletion {
    box-shadow: 0 0 0 1px var(--stk-danger-50);
    border-radius: 0.5rem;
    margin: 1px;

    &::after {
      content: "";
      display: block;
      position: absolute;
      background-image: linear-gradient(135deg,
        var(--stk-danger-70) 25%,
        var(--stk-danger-80) 25%,
        var(--stk-danger-80) 50%,
        var(--stk-danger-70) 50%,
        var(--stk-danger-70) 75%,
        var(--stk-danger-80) 75%,
        var(--stk-danger-80) 100%
      );
      background-size: 40px 40px;
      inset: 0;
      border-radius: 0.5rem;
      opacity: 0.7;
    }
  }

  &.has-conflicts .field-row {
    background-color: var(--stk-danger-95);
    color: var(--stk-danger-30);
  }
}

.show-confidence {
  &.value-type-string,
  &.value-type-enum,
  &.value-type-boolean,
  &.value-type-integer {
    display: none;
  }

  &.show-high-confidence {
    &.confidence-high{
      display: block;
    }
  }

  &.show-medium-confidence {
    &.confidence-medium{
      display: block;
    }
  }

  &.show-low-confidence {
    &.confidence-low{
      display: block;
    }
  }
}

.show-changes {
  &.is-top-level,
  &.value-type-object,
  &.value-type-array {
    /* Hide objects with changes, unless one child has changes */
    &.does-not-have-changes:not(.child-object-has-changes) {
      display: none;
    }
  }

  &.does-not-have-changes {
    &.value-type-string,
    &.value-type-enum,
    &.value-type-boolean,
    &.value-type-integer {
      display: none;
    }
  }
}

.show-context {
  &.is-top-level,
  &.value-type-object,
  &.value-type-array {
    /* Hide objects with changes, unless one child has changes */
    &.does-not-have-changes:not(.child-object-has-changes) {
      display: none;
    }
  }

  &.does-not-have-changes > .field-object > .does-not-have-changes {
    &.value-type-string,
    &.value-type-enum,
    &.value-type-boolean,
    &.value-type-integer {
      display: none;
    }
  }
}

.has-conflicts {
  display: block !important;
}

.child-object-has-changes {
  display: block !important;
}

.field-row {
  align-items: center;
  display: grid;
  grid-template-columns: 1fr 6rem 14rem 1.25rem 14rem 12rem 2.5rem;
  min-height: 2em;
  padding: 0.25rem 0.75rem;
  border-radius: 0.5rem;
  gap: 0.5rem;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &:hover {
    background-color: var(--stk-background-alt);
    color: var(--stk-text-strong);

    > .field-toggle {
      opacity: 1 !important;
    }
  }
}

.field-control {
  display: flex;
  align-items: center;
}

.field-arrow {
  color: var(--stk-primary);

  &.is-denied {
    transform: scaleX(-1);
    color: var(--stk-text-weak);
    opacity: 0.5;
  }
}

.field-input {
  &.is-denied {
    color: var(--stk-text-weak);
    border-color: transparent !important;
    background-color: transparent !important;
    box-shadow: none !important;
    text-decoration: line-through;

    + .field-select-icon {
      opacity: 0;
    }
  }
}

.field-option {
  justify-content: space-between;
}

.field-toggle {
  display: flex;
}

.change-type-both_empty,
.change-type-same {
  color: var(--stk-text-weak);
  font-size: 0.875rem;

  &:hover {
    opacity: 1;
  }

  .field-arrow,
  .field-toggle {
    opacity: 0;
  }

  .field-control.is-current {
    .stk-select select,
    .stk-input {
      background-color: transparent;
      border-color: transparent;
      box-shadow: none;
    }

    .stk-select .stk-icon {
      opacity: 0;
    }
  }

  &:not(:hover) {
    .field-control.is-draft {
      .stk-select select,
      .stk-input {
        background-color: transparent;
        border-color: transparent;
        box-shadow: none;
      }

      .stk-select .stk-icon {
        opacity: 0;
      }
    }
  }
}

.change-type-both_empty {
  .field-control.is-current {
    opacity: 0;
    pointer-events: none;
  }
}

.change-type-same {
  .field-control.is-current {
    opacity: 0.3;
    pointer-events: none;
  }
}
</style>
